<template>
    <section class="grid justify-center mb-4 px-4 md:px-8">
        <!-- Clients List -->
        <div class="my-6">
            <div class="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h1 class="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Clientes</h1>
                <InputText
                    v-model="searchQuery"
                    placeholder="Buscar cliente por nombre o ID..."
                    class="w-full sm:w-80"
                    icon="pi pi-search"
                />
            </div>
            <div v-if="loadingClients" class="text-center py-8">
                <i class="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
                <p class="mt-2 text-zinc-600 dark:text-zinc-400">Cargando clientes...</p>
            </div>
            <div v-else-if="filteredClients.length === 0 && searchQuery" class="text-center py-8 text-zinc-600 dark:text-zinc-400">
                No se encontraron clientes que coincidan con "{{ searchQuery }}"
            </div>
            <div v-else-if="clients.length === 0" class="text-center py-8 text-zinc-600 dark:text-zinc-400">
                No se encontraron clientes
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div 
                    v-for="client in filteredClients" 
                    :key="client.id"
                    @click="selectClient(client)"
                    class="p-4 rounded-lg border-2 cursor-pointer transition-all border-zinc-300 dark:border-zinc-600 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-zinc-800"
                >
                    <div class="flex items-center gap-3">
                        <i class="pi pi-building text-2xl text-blue-500"></i>
                        <div class="flex flex-col items-start">
                            <p class="font-semibold text-zinc-900 dark:text-zinc-100">{{ client.name }}</p>
                            <p class="text-sm text-zinc-500 dark:text-zinc-400">ID: {{ client.id }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Dialog for Legal Names -->
        <Dialog 
            v-model:visible="showLegalNamesDialog" 
            modal 
            :header="`Razones Sociales de: ${selectedClient?.name || ''}`" 
            :style="{ width: '90vw', maxWidth: '1200px' }"
            :dismissableMask="true"
            @hide="clearSelection"
        >
            <div v-if="loadingLegalNames" class="text-center py-8">
                <i class="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
                <p class="mt-2 text-zinc-600 dark:text-zinc-400">Cargando razones sociales...</p>
            </div>
            <div v-else-if="legalNames.length === 0" class="text-center py-8 text-zinc-600 dark:text-zinc-400">
                <p class="mb-4">Este cliente no tiene razones sociales registradas</p>
                <Button 
                    label="Agregar Primera Razón Social" 
                    icon="pi pi-plus"
                    @click="addNewLegalName"
                    class="p-button-success"
                />
            </div>
            <div v-else>
                <div class="mb-4 flex justify-between items-center">
                    <span class="text-sm text-zinc-600 dark:text-zinc-400">
                        {{ legalNames.length }} razón(es) social(es) encontrada(s)
                    </span>
                    <Button 
                        label="Agregar Nueva" 
                        icon="pi pi-plus"
                        @click="addNewLegalName"
                        class="p-button-success p-button-sm"
                    />
                </div>
                <DataTable 
                    :value="legalNames"
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                    class="legal-names-table"
                    :paginator="legalNames.length > 10"
                    :rows="10"
                >
                    <Column field="id" header="ID" :sortable="true" headerStyle="width: 80px"></Column>
                    <Column field="name" header="Razón Social" :sortable="true">
                        <template #body="slotProps">
                            <div 
                                v-if="editingNameId !== slotProps.data.id"
                                @click="startEditingName(slotProps.data)"
                                class="editable-cell"
                                :title="'Click para editar nombre'"
                            >
                                <span class="font-semibold">{{ slotProps.data.name }}</span>
                            </div>
                            <div v-else>
                                <InputText 
                                    v-model="editingNameValue"
                                    @keyup.enter="saveCurrentEdit(slotProps.data)"
                                    @keyup.esc="cancelCurrentEdit"
                                    class="name-input w-full"
                                    ref="nameInput"
                                    type="text"
                                    placeholder="Nombre de la razón social"
                                    :disabled="savingLegalName"
                                />
                            </div>
                        </template>
                    </Column>
                    <Column field="rfc" header="RFC" :sortable="true">
                        <template #body="slotProps">
                            <div 
                                v-if="editingRfcId !== slotProps.data.id"
                                @click="startEditingRfc(slotProps.data)"
                                class="editable-cell"
                                :title="'Click para editar RFC'"
                            >
                                <span v-if="slotProps.data.rfc">{{ slotProps.data.rfc }}</span>
                                <span v-else class="text-zinc-400 dark:text-zinc-500 italic">Sin RFC</span>
                            </div>
                            <div v-else>
                                <InputText 
                                    v-model="editingRfcValue"
                                    @keyup.enter="saveCurrentEdit(slotProps.data)"
                                    @keyup.esc="cancelCurrentEdit"
                                    class="rfc-input w-full"
                                    ref="rfcInput"
                                    type="text"
                                    placeholder="RFC"
                                    :disabled="savingLegalName"
                                />
                            </div>
                        </template>
                    </Column>
                    <Column header="Acciones" headerStyle="width: 150px">
                        <template #body="slotProps">
                            <!-- Botones de edición activa -->
                            <div v-if="editingNameId === slotProps.data.id || editingRfcId === slotProps.data.id" class="flex gap-2 justify-center">
                                <Button 
                                    icon="pi pi-check" 
                                    @click="saveCurrentEdit(slotProps.data)"
                                    class="p-button-success p-button-sm"
                                    title="Guardar cambios"
                                    :disabled="savingLegalName"
                                />
                                <Button 
                                    icon="pi pi-times" 
                                    @click="cancelCurrentEdit"
                                    class="p-button-danger p-button-sm"
                                    title="Cancelar edición"
                                    :disabled="savingLegalName"
                                />
                            </div>
                            <!-- Botones normales -->
                            <div v-else class="flex gap-2 justify-center">
                                <Button 
                                    icon="pi pi-pencil" 
                                    class="p-button-rounded p-button-text p-button-info"
                                    @click="startEditingBoth(slotProps.data)"
                                    title="Editar nombre y RFC"
                                />
                                <Button 
                                    icon="pi pi-trash" 
                                    class="p-button-rounded p-button-text p-button-danger"
                                    @click="deleteLegalName(slotProps.data)"
                                    title="Eliminar razón social"
                                    :disabled="savingLegalName"
                                />
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </Dialog>
        
        <ConfirmDialog></ConfirmDialog>
    </section>
</template>

<script>
import axiosInstance from '../axiosInstance';

// Constants
const TOAST_LIFETIMES = {
    SUCCESS: 3000,
    WARNING: 5000,
    ERROR: 5000
};

const TOAST_GROUP = 'topr';

const API_ENDPOINTS = {
    CLIENTS_LIST: '/api/clients_list',
    CLIENT_LEGAL_NAMES: '/api/client_legal_names',
    LEGAL_NAME_SAVE: '/api/legal_name_save',
    LEGAL_NAME_DELETE: '/api/legal_name_kill'
};

export default {
    name: 'RazonesSociales',
    emits: ['update-loading'],
    data() {
        return {
            clients: [],
            legalNames: [],
            selectedClient: null,
            loadingClients: false,
            loadingLegalNames: false,
            editingNameId: null,
            editingRfcId: null,
            editingNameValue: '',
            editingRfcValue: '',
            savingLegalName: false,
            searchQuery: '',
            showLegalNamesDialog: false
        }
    },
    computed: {
        /**
         * Filters clients based on search query
         * @returns {Array} Filtered clients array
         */
        filteredClients() {
            if (!this.searchQuery) {
                return this.clients;
            }

            const query = this.searchQuery.toLowerCase().trim();
            return this.clients.filter(client => {
                const nameMatch = client.name.toLowerCase().includes(query);
                const idMatch = client.id.toString().includes(query);
                return nameMatch || idMatch;
            });
        }
    },
    methods: {
        /**
         * Fetches the list of clients from the API
         */
        async fetchClients() {
            this.loadingClients = true;
            try {
                const response = await axiosInstance.post(API_ENDPOINTS.CLIENTS_LIST, {
                    token: localStorage.getItem('token')
                });

                if (response.data?.success) {
                    // Extract items array from the response
                    this.clients = response.data?.items || [];
                    console.log("Clientes obtenidos exitosamente:", this.clients.length);
                } else {
                    console.warn("fetchClients: Problema al obtener clientes", response);
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema al obtener los clientes.', 
                        life: TOAST_LIFETIMES.WARNING, 
                        group: TOAST_GROUP 
                    });
                }
            } catch(error) {
                console.error("fetchClients: Error al obtener clientes", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al obtener los clientes.', 
                    life: TOAST_LIFETIMES.ERROR, 
                    group: TOAST_GROUP 
                });
            } finally {
                this.loadingClients = false;
            }
        },

        /**
         * Fetches legal names for a specific client
         * @param {number} clientId - The ID of the client
         */
        async fetchLegalNames(clientId) {
            this.loadingLegalNames = true;
            this.legalNames = [];
            try {
                const response = await axiosInstance.post(API_ENDPOINTS.CLIENT_LEGAL_NAMES, {
                    token: localStorage.getItem('token'),
                    client_id: clientId
                });

                if (response.data?.success) {
                    this.legalNames = response.data.legal_names || [];
                    console.log("Razones sociales obtenidas:", this.legalNames.length);
                } else {
                    console.warn("fetchLegalNames: Problema al obtener razones sociales", response);
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema al obtener las razones sociales.', 
                        life: TOAST_LIFETIMES.WARNING, 
                        group: TOAST_GROUP
                    });
                }
            } catch(error) {
                console.error("fetchLegalNames: Error al obtener razones sociales", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al obtener las razones sociales.', 
                    life: TOAST_LIFETIMES.ERROR, 
                    group: TOAST_GROUP 
                });
            } finally {
                this.loadingLegalNames = false;
            }
        },

        /**
         * Handles client selection
         * @param {Object} client - The selected client object
         */
        selectClient(client) {
            this.selectedClient = client;
            this.showLegalNamesDialog = true;
            this.fetchLegalNames(client.id);
        },

        /**
         * Clears the current client selection and resets all states
         */
        clearSelection() {
            // Cancel any ongoing edit first
            this.cancelCurrentEdit();
            
            // Then clear the dialog and client data
            this.showLegalNamesDialog = false;
            this.selectedClient = null;
            this.legalNames = [];
        },

        /**
         * Adds a new legal name for the current client
         */
        addNewLegalName() {
            // Create a temporary new legal name object
            const newLegalName = {
                id: 'new-' + Date.now(), // Temporary ID
                name: '',
                rfc: '',
                client_id: this.selectedClient.id,
                isNew: true
            };
            
            // Add to the beginning of the array
            this.legalNames.unshift(newLegalName);
            
            // Start editing immediately
            this.startEditingBoth(newLegalName);
        },

        /**
         * Starts editing mode for legal name
         * @param {Object} legalName - The legal name object to edit
         */
        startEditingName(legalName) {
            this.editingNameId = legalName.id;
            this.editingNameValue = legalName.name || '';
            this.focusInput('nameInput');
        },

        /**
         * Starts editing mode for RFC field only
         * @param {Object} legalName - The legal name object to edit
         */
        startEditingRfc(legalName) {
            this.editingRfcId = legalName.id;
            this.editingRfcValue = legalName.rfc || '';
            this.focusInput('rfcInput');
        },

        /**
         * Starts editing mode for both name and RFC fields
         * @param {Object} legalName - The legal name object to edit
         */
        startEditingBoth(legalName) {
            this.editingNameId = legalName.id;
            this.editingRfcId = legalName.id;
            this.editingNameValue = legalName.name || '';
            this.editingRfcValue = legalName.rfc || '';
            this.focusInput('nameInput');
        },

        /**
         * Helper method to focus input elements
         * @param {string} refName - The name of the ref to focus
         */
        focusInput(refName) {
            this.$nextTick(() => {
                const ref = this.$refs[refName];
                if (ref && ref.length > 0) {
                    ref[0].$el.focus();
                } else if (ref) {
                    ref.$el.focus();
                }
            });
        },

        /**
         * Saves the legal name edit to the database
         * @param {Object} legalName - The legal name object being edited
         */
        async saveLegalNameEdit(legalName) {
            if (this.savingLegalName || !this.selectedClient || !legalName) return;

            // Validate required fields
            const cleanedName = this.editingNameValue.trim();
            const cleanedRfc = this.editingRfcValue.replace(/\s+/g, '').toUpperCase();
            
            if (!cleanedName) {
                this.$toast.add({ 
                    severity: 'warn', 
                    summary: 'Aviso',
                    detail: 'El nombre de la razón social es requerido.', 
                    life: 3000, 
                    group: 'topr' 
                });
                return;
            }

            this.savingLegalName = true;
            try {
                const payload = {
                    token: localStorage.getItem('token'),
                    name: cleanedName,
                    rfc: cleanedRfc,
                    client_id: this.selectedClient.id
                };

                // Add ID only for existing legal names (updates)
                if (!legalName.isNew) {
                    payload.id = legalName.id;
                }

                const response = await axiosInstance.post("/api/legal_name_save", payload);

                if (response.data.success) {
                    // If it's a new legal name, replace with the response data
                    if (legalName.isNew) {
                        const index = this.legalNames.findIndex(ln => ln.id === legalName.id);
                        if (index !== -1) {
                            // Create new legal name object from response data
                            const newLegalName = {
                                id: response.data.id,
                                name: response.data.name,
                                rfc: response.data.rfc,
                                client_id: response.data.client_id
                            };
                            this.legalNames.splice(index, 1, newLegalName);
                        }
                    } else {
                        // Update existing legal name
                        const index = this.legalNames.findIndex(ln => ln.id === legalName.id);
                        if (index !== -1) {
                            this.legalNames[index].name = cleanedName;
                            this.legalNames[index].rfc = cleanedRfc;
                        }
                    }
                    
                    console.log("Razón social guardada exitosamente:", cleanedName);
                    this.$toast.add({ 
                        severity: 'success', 
                        summary: 'Éxito',
                        detail: legalName.isNew ? 'Razón social creada correctamente.' : 'Razón social actualizada correctamente.', 
                        life: TOAST_LIFETIMES.SUCCESS, 
                        group: TOAST_GROUP 
                    });
                    
                    this.cancelLegalNameEdit();
                } else {
                    console.warn("saveLegalNameEdit: Problema al guardar razón social", response);
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema al guardar la razón social.', 
                        life: 5000, 
                        group: 'topr' 
                    });
                }
            } catch(error) {
                console.error("saveLegalNameEdit: Error al guardar razón social", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al guardar la razón social.', 
                    life: 5000, 
                    group: 'topr' 
                });
            } finally {
                this.savingLegalName = false;
            }
        },



        /**
         * Cancels editing for both fields and resets all editing states
         */
        cancelLegalNameEdit() {
            // If it was a new legal name being created, remove it
            if ((this.editingNameId && this.editingNameId.toString().startsWith('new-')) ||
                (this.editingRfcId && this.editingRfcId.toString().startsWith('new-'))) {
                const targetId = this.editingNameId || this.editingRfcId;
                const index = this.legalNames.findIndex(ln => ln.id === targetId);
                if (index !== -1) {
                    this.legalNames.splice(index, 1);
                }
            }
            
            this.editingNameId = null;
            this.editingRfcId = null;
            this.editingNameValue = '';
            this.editingRfcValue = '';
        },

        /**
         * Saves the current edit (detects which field is being edited)
         */
        async saveCurrentEdit(legalName) {
            if (this.savingLegalName || !this.selectedClient || !legalName) return;

            const isEditingName = this.editingNameId === legalName.id;
            const isEditingRfc = this.editingRfcId === legalName.id;

            if (isEditingName && isEditingRfc) {
                // Both fields are being edited - use the complete save method
                await this.saveLegalNameEdit(legalName);
            } else {
                // Single field edit - handle inline
                const cleanedName = this.editingNameValue.trim();
                const cleanedRfc = this.editingRfcValue.replace(/\s+/g, '').toUpperCase();
                
                // Validate if editing name
                if (isEditingName && !cleanedName) {
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'El nombre de la razón social es requerido.', 
                        life: 3000, 
                        group: 'topr' 
                    });
                    return;
                }

                this.savingLegalName = true;
                try {
                    const payload = {
                        token: localStorage.getItem('token'),
                        name: isEditingName ? cleanedName : (legalName.name || ''),
                        rfc: isEditingRfc ? cleanedRfc : (legalName.rfc || ''),
                        client_id: this.selectedClient.id
                    };

                    if (!legalName.isNew) {
                        payload.id = legalName.id;
                    }

                    const response = await axiosInstance.post("/api/legal_name_save", payload);

                    if (response.data.success) {
                        if (legalName.isNew) {
                            const index = this.legalNames.findIndex(ln => ln.id === legalName.id);
                            if (index !== -1) {
                                const newLegalName = {
                                    id: response.data.id,
                                    name: response.data.name,
                                    rfc: response.data.rfc,
                                    client_id: response.data.client_id
                                };
                                this.legalNames.splice(index, 1, newLegalName);
                            }
                        } else {
                            const index = this.legalNames.findIndex(ln => ln.id === legalName.id);
                            if (index !== -1) {
                                if (isEditingName) this.legalNames[index].name = cleanedName;
                                if (isEditingRfc) this.legalNames[index].rfc = cleanedRfc;
                            }
                        }
                        
                        const fieldName = isEditingName ? 'Nombre' : 'RFC';
                        this.$toast.add({ 
                            severity: 'success', 
                            summary: 'Éxito',
                            detail: `${fieldName} actualizado correctamente.`, 
                            life: 3000, 
                            group: 'topr' 
                        });
                        
                        this.cancelCurrentEdit();
                    } else {
                        this.$toast.add({ 
                            severity: 'warn', 
                            summary: 'Aviso',
                            detail: 'Hubo un problema al guardar los cambios.', 
                            life: 5000, 
                            group: 'topr' 
                        });
                    }
                } catch(error) {
                    console.error("saveCurrentEdit: Error al guardar", error);
                    this.$toast.add({ 
                        severity: 'error', 
                        summary: 'Error',
                        detail: 'Error al guardar los cambios.', 
                        life: 5000, 
                        group: 'topr' 
                    });
                } finally {
                    this.savingLegalName = false;
                }
            }
        },

        /**
         * Cancels the current edit (detects which field is being edited)
         */
        cancelCurrentEdit() {
            // If it was a new legal name being created, remove it
            if ((this.editingNameId && this.editingNameId.toString().startsWith('new-')) ||
                (this.editingRfcId && this.editingRfcId.toString().startsWith('new-'))) {
                const targetId = this.editingNameId || this.editingRfcId;
                const index = this.legalNames.findIndex(ln => ln.id === targetId);
                if (index !== -1) {
                    this.legalNames.splice(index, 1);
                }
            }
            
            // Reset all editing states
            this.editingNameId = null;
            this.editingRfcId = null;
            this.editingNameValue = '';
            this.editingRfcValue = '';
        },

        /**
         * Deletes a legal name
         * @param {Object} legalName - The legal name object to delete
         */
        deleteLegalName(legalName) {
            // If it's a new (unsaved) legal name, just remove it locally
            if (legalName.isNew) {
                const index = this.legalNames.findIndex(ln => ln.id === legalName.id);
                if (index !== -1) {
                    this.legalNames.splice(index, 1);
                }
                return;
            }

            // Show confirmation dialog
            this.$confirm.require({
                header: 'Confirmación de eliminación',
                message: `¿Estás seguro de que deseas eliminar la razón social "${legalName.name}"?`,
                icon: 'pi pi-exclamation-triangle',
                rejectLabel: 'Cancelar',
                acceptLabel: 'Aceptar',
                rejectClass: 'p-button-secondary p-button-outlined',
                acceptClass: 'p-button-danger',
                accept: () => {
                    this.performLegalNameDeletion(legalName);
                }
            });
        },

        /**
         * Performs the actual deletion of a legal name after confirmation
         * @param {Object} legalName - The legal name object to delete
         */
        async performLegalNameDeletion(legalName) {
            this.savingLegalName = true;
            try {
                const response = await axiosInstance.post("/api/legal_name_kill", {
                    token: localStorage.getItem('token'),
                    id: legalName.id
                });

                if (response.data.success) {
                    // Remove from local array
                    const index = this.legalNames.findIndex(ln => ln.id === legalName.id);
                    if (index !== -1) {
                        this.legalNames.splice(index, 1);
                    }
                    
                    console.log("Razón social eliminada exitosamente:", legalName.name);
                    this.$toast.add({ 
                        severity: 'success', 
                        summary: 'Éxito',
                        detail: 'Razón social eliminada correctamente.', 
                        life: 3000, 
                        group: 'topr' 
                    });
                } else {
                    console.warn("deleteLegalName: Problema al eliminar razón social", response);
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema al eliminar la razón social.', 
                        life: 5000, 
                        group: 'topr' 
                    });
                }
            } catch(error) {
                console.error("deleteLegalName: Error al eliminar razón social", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al eliminar la razón social.', 
                    life: 5000, 
                    group: 'topr' 
                });
            } finally {
                this.savingLegalName = false;
            }
        }
    },
    mounted() {
        this.$emit("update-loading", true);
        this.fetchClients().finally(() => {
            this.$emit("update-loading", false);
        });
    }
}
</script>

<style scoped>
    /* Editable cell styling */
    .editable-cell {
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 0.25rem;
        transition: background-color 0.2s;
        min-height: 2rem;
    }

    .editable-cell:hover {
        background-color: #dbeafe;
    }

    /* Input styling */
    .rfc-input {
        text-transform: uppercase;
    }

    /* DataTable styling */
    ::v-deep(.legal-names-table.p-datatable) {
        border-radius: 0.5rem !important;
        overflow: hidden !important;
        box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) !important;
    }

    ::v-deep(.legal-names-table.p-datatable .p-datatable-wrapper) {
        border-radius: 0.5rem !important;
        overflow: hidden !important;
    }

    ::v-deep(.legal-names-table.p-datatable .p-datatable-thead > tr > th) {
        background-color: #0f1338 !important;
        color: white !important;
        font-size: 1rem !important;
        font-weight: 600 !important;
        padding: 0.75rem 1rem !important;
        border-bottom: 2px solid #374151 !important;
    }

    ::v-deep(.legal-names-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td) {
        background-color: #f3f4f6 !important;
        color: #111827 !important;
    }

    ::v-deep(.legal-names-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td) {
        background-color: #ffffff !important;
        color: #111827 !important;
    }

    ::v-deep(.legal-names-table.p-datatable .p-datatable-tbody > tr > td) {
        padding: 0.5rem 1rem !important;
        border-bottom: 1px solid #e5e7eb !important;
        vertical-align: middle !important;
    }

    ::v-deep(.legal-names-table.p-datatable .p-datatable-tbody > tr) {
        transition: background-color 0.3s ease !important;
    }

    ::v-deep(.legal-names-table.p-datatable .p-datatable-tbody > tr:hover > td) {
        background-color: #fef3c7 !important;
        color: #111827 !important;
    }

</style>

<style>
.dark .legal-names-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .legal-names-table.p-datatable .p-datatable-thead > tr > th .p-sortable-column-icon {
    color: #0f1338 !important;
}

.dark .legal-names-table.p-datatable .p-datatable-thead > tr > th.p-highlight .p-sortable-column-icon {
    color: #3b82f6 !important;
}

.dark .legal-names-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .legal-names-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .legal-names-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .legal-names-table.p-datatable .p-datatable-tbody > tr:hover > td {
    background-color: #422006 !important;
    color: white !important;
}

.dark .editable-cell:hover {
    background-color: #1e3a8a !important;
    color: white !important;
}
</style>
